import React, { useState } from 'react';
import { META_ONBOARDING_COMPLETED } from 'components/AboutYourself/components/AboutYourselfForm/hooks/useAboutYourselfForm';
import Form from 'components/Form';
import { FormikHelpers, FormikValues } from 'formik';
import useFormikSubmit from 'hooks/useFormikSubmit';
import useAuthentication from 'modules/auth/hooks/useAuthentication';
import useInstanceConfig from 'modules/siteConfig/hooks/useInstanceConfig';
import Link from 'next/link';
import { FetchingStatus } from 'types/enums/FetchType';

import { Box, Button, CircularProgress as MaterialCircularProgress, Typography } from '@lyearn/ui';

import { formFields, formInitialValues, formLayout, formValidate } from './fields';
import Header from './Header';

const SignupForm = (props: {
  switchToLogin?: Function;
  redirectPath?: string;
  handleClose?: Function;
}) => {
  const { fetchStatus, handleLookupEmail, handleSignUp } = useAuthentication();
  const [formikSubmit, setFormikSubmit] = useFormikSubmit();
  const [submissible, setSubmissible] = useState(false);
  const { site } = useInstanceConfig();

  const { switchToLogin = () => {}, redirectPath = '/', handleClose = () => {} } = props;

  const {
    fields = formFields,
    initialValues = formInitialValues,
    validate = formValidate,
    layout = formLayout,
  } = site.signupForm || {};
  const skipOnBoarding = site.skipUserOnBoarding || false;

  const onSubmit = async (
    values: FormikValues,
    formHelpers: FormikHelpers<{
      [key: string]: any;
    }>,
  ) => {
    const { password, email: emailId, ...rest } = values;
    const { setFieldError } = formHelpers;
    const emailError = await (handleLookupEmail as any)({ emailId });

    if (!emailError) {
      setFieldError(
        'email',
        (
          <Box component="span">
            Account already exists. Try{' '}
            <Link
              className="textlink-external underline"
              href={'/login?redirectPath=' + encodeURIComponent(redirectPath)}>
              logging in
            </Link>
          </Box>
        ) as any,
      );
    } else {
      await (handleSignUp as any)(
        {
          emailId,
          password,
          meta: JSON.stringify({ [META_ONBOARDING_COMPLETED]: skipOnBoarding }),
          ...rest,
          subDomain: site.subdomain,
          storeName: site.storeName,
          host: window.location.host,
        },
        handleClose,
        redirectPath,
      );
    }
  };

  const onFormChange: Function = ({ values, isValid }: any) => {
    if (values.email && values.password && isValid) {
      setSubmissible(true);
    } else {
      setSubmissible(false);
    }
  };

  return (
    <>
      <Header switchToLogin={switchToLogin} />
      <Form
        fields={fields}
        formName="Sign up"
        initialValues={initialValues}
        layout={layout}
        setSubmitRef={setFormikSubmit}
        validate={validate}
        onChange={onFormChange}
        onSubmit={onSubmit}
      />
      <Button
        className="my-10 mt-32 w-full normal-case"
        disabled={!submissible || fetchStatus === FetchingStatus.Fetching}
        size="large"
        variant="filled"
        onClick={formikSubmit as any}>
        <Typography className="heading-xs flex items-center">
          Create Account
          {fetchStatus === FetchingStatus.Fetching ? (
            <MaterialCircularProgress
              className="fill-current text-text-white ms-10"
              size="x-small"
            />
          ) : null}
        </Typography>
      </Button>
    </>
  );
};

export default SignupForm;
